import ElectromagneticSpectrumImg from '../../Images/ElectromagneticSpectrumImg.jpg'

// Electromagnetic Spectrum — Physics Reference Page
// Style matches the Magnetism reference page (Georgia serif, red accent #c0392b)

const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Full EM Spectrum — frequency/wavelength bar ────────────
function DiagramSpectrumBar() {
  const W = 900, H = 320;

  // Bands: label, color, x position start (fraction of W), width fraction
  const bands = [
    { name:'Radio',    color:'#1a3a7c', textCol:'#fff', freq:'< 3 GHz',      wave:'> 10 cm',    x:0,    w:130 },
    { name:'Micro-\nwave', color:'#2E75B6', textCol:'#fff', freq:'3 GHz–300 GHz', wave:'1mm–10cm', x:130,  w:110 },
    { name:'Infrared', color:'#e07b00', textCol:'#fff', freq:'300 GHz–430 THz',wave:'700nm–1mm',  x:240,  w:120 },
    { name:'Visible',  color:'#c0392b', textCol:'#fff', freq:'430–770 THz',   wave:'380–700nm',  x:360,  w:100 },
    { name:'Ultraviolet',color:'#8e44ad',textCol:'#fff', freq:'770 THz–30 PHz',wave:'10–380nm',  x:460,  w:120 },
    { name:'X-ray',    color:'#16a085', textCol:'#fff', freq:'30 PHz–30 EHz', wave:'0.01–10nm',  x:580,  w:110 },
    { name:'Gamma\nRay',  color:'#c0392b', textCol:'#fff', freq:'> 30 EHz',      wave:'< 0.01nm',  x:690,  w:110 },
  ];

  // Visible light sub-bar
  const visColors = [
    { c:'#6600ff', label:'Violet', x:360, w:14 },
    { c:'#4400ff', label:'Indigo', x:374, w:13 },
    { c:'#0077ff', label:'Blue',   x:387, w:16 },
    { c:'#00cc44', label:'Green',  x:403, w:16 },
    { c:'#ffee00', label:'Yellow', x:419, w:13 },
    { c:'#ff8800', label:'Orange', x:432, w:13 },
    { c:'#ff2200', label:'Red',    x:445, w:15 },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Electromagnetic Spectrum — Frequency, Wavelength and Band Overview</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Background */}
        <rect x={0} y={0} width={W} height={H} fill="#fffafa" rx={6}/>

        {/* Frequency axis arrow (top) */}
        <text x={10} y={22} fontSize="13" fill="#7B0D0D" fontWeight="700">Increasing frequency →</text>
        <line x1={0} y1={28} x2={800} y2={28} stroke="#c0392b" strokeWidth="1.5"/>
        <polygon points="800,23 810,28 800,33" fill="#c0392b"/>

        {/* Wavelength axis arrow (bottom, reversed) */}
        <text x={10} y={H-8} fontSize="13" fill="#2E75B6" fontWeight="700">← Increasing wavelength</text>
        <line x1={800} y1={H-18} x2={0} y2={H-18} stroke="#2E75B6" strokeWidth="1.5"/>
        <polygon points="0,H-23 -10,H-18 0,H-13" fill="#2E75B6"/>

        {/* Band rectangles */}
        {bands.map((b, i) => (
          <g key={i}>
            <rect x={b.x} y={35} width={b.w} height={120} fill={b.color} opacity={i===3?0.5:0.82} rx={2}/>
            {b.name.split('\n').map((line, li) => (
              <text key={li} x={b.x + b.w/2} y={75 + li*18 - (b.name.includes('\n')?8:0)}
                textAnchor="middle" fontSize="12" fill={b.textCol} fontWeight="700">{line}</text>
            ))}
            {/* Freq label below band */}
            <text x={b.x + b.w/2} y={170} textAnchor="middle" fontSize="9.5" fill="#555"
              transform={`rotate(-35, ${b.x + b.w/2}, 170)`}>{b.freq}</text>
            {/* Wavelength label */}
            <text x={b.x + b.w/2} y={220} textAnchor="middle" fontSize="9.5" fill="#2E75B6"
              transform={`rotate(35, ${b.x + b.w/2}, 220)`}>{b.wave}</text>
          </g>
        ))}

        {/* Visible light rainbow detail */}
        {visColors.map((v, i) => (
          <g key={i}>
            <rect x={v.x} y={155} width={v.w} height={26} fill={v.c} opacity="0.9"/>
          </g>
        ))}
        <text x={410} y={196} textAnchor="middle" fontSize="10" fill="#7B0D0D" fontStyle="italic">↑ Visible light expanded</text>

        {/* Right panel: Key properties */}
        <rect x={810} y={35} width={85} height={120} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={852} y={52} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">All EM waves:</text>
        {[
          "Travel at c",
          "= 3×10⁸ m/s",
          "in vacuum",
          "",
          "Transverse",
          "waves",
          "",
          "c = fλ",
        ].map((t,i)=>(
          <text key={i} x={816} y={64+i*13} fontSize="10" fill="#333">{t}</text>
        ))}

        {/* Energy arrow */}
        <text x={10} y={270} fontSize="12" fill="#8e44ad" fontWeight="700">← Lower energy (longer λ)</text>
        <text x={490} y={270} fontSize="12" fill="#c0392b" fontWeight="700">Higher energy (shorter λ) →</text>
        <line x1={0} y1={276} x2={900} y2={276} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>

        {/* Photon energy formula */}
        <rect x={340} y={283} width={220} height={28} rx={4} fill="#FEF0F0" stroke="#c0392b"/>
        <text x={450} y={302} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontFamily="'Courier New', monospace" fontWeight="700">E = hf = hc/λ</text>
      </svg>
      <div style={S.dCaption}>
        The EM spectrum spans over 20 orders of magnitude in frequency. All bands are the same type of wave — oscillating electric and magnetic fields — differing only in frequency (and therefore wavelength and photon energy). Visible light occupies less than 1% of the full spectrum.
      </div>
    </div>
  );
}

// ── Diagram 2: EM Wave Structure ──────────────────────────────────────
function DiagramWaveStructure() {
  const W = 900, H = 300;
  const cx = 450, cy = 150;

  // Sinusoidal wave for E field (vertical oscillation)
  const pts = [];
  for (let i = 0; i <= 800; i += 4) {
    const x = 50 + i;
    const y = cy - Math.sin((i / 800) * 4 * Math.PI) * 70;
    pts.push(`${x},${y}`);
  }
  const ePath = 'M ' + pts.join(' L ');

  // B field (horizontal oscillation — into/out of plane, shown offset)
  const bPts = [];
  for (let i = 0; i <= 800; i += 4) {
    const x = 50 + i;
    const y = cy - Math.sin((i / 800) * 4 * Math.PI) * 45;
    bPts.push(`${x},${y + 55}`);
  }
  const bPath = 'M ' + bPts.join(' L ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Structure of an Electromagnetic Wave — Electric and Magnetic Fields</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Propagation direction */}
        <line x1={30} y1={cy} x2={870} y2={cy} stroke="#aaa" strokeWidth="1.5" strokeDasharray="6,3"/>
        <polygon points="870,145 885,150 870,155" fill="#888"/>
        <text x={840} y={135} fontSize="12" fill="#888" fontStyle="italic">direction</text>
        <text x={840} y={148} fontSize="12" fill="#888" fontStyle="italic">of travel</text>
        <text x={25} y={cy+4} fontSize="13" fill="#888">→</text>

        {/* E field wave */}
        <path d={ePath} fill="none" stroke="#c0392b" strokeWidth="2.5"/>
        <text x={60} y={55} fontSize="13" fill="#c0392b" fontWeight="700">E  (electric field — oscillates vertically)</text>

        {/* B field wave */}
        <path d={bPath} fill="none" stroke="#2E75B6" strokeWidth="2.5" opacity="0.85"/>
        <text x={60} y={H-18} fontSize="13" fill="#2E75B6" fontWeight="700">B  (magnetic field — oscillates horizontally, ⊥ to E)</text>

        {/* Wavelength brace */}
        <line x1={50} y1={30} x2={250} y2={30} stroke="#7B0D0D" strokeWidth="1.2"/>
        <line x1={50} y1={25} x2={50} y2={35} stroke="#7B0D0D" strokeWidth="1.2"/>
        <line x1={250} y1={25} x2={250} y2={35} stroke="#7B0D0D" strokeWidth="1.2"/>
        <text x={150} y={22} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontStyle="italic">λ (wavelength)</text>

        {/* Amplitude label */}
        <line x1={160} y1={cy} x2={160} y2={cy-70} stroke="#c0392b" strokeWidth="1" strokeDasharray="3,2"/>
        <text x={164} y={cy-34} fontSize="11" fill="#c0392b" fontStyle="italic">A</text>

        {/* Key labels */}
        <rect x={680} y={40} width={200} height={100} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={780} y={58} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Key relationships:</text>
        <text x={686} y={75} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">c = fλ = 3×10⁸ m/s</text>
        <text x={686} y={91} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">E = hf  (photon energy)</text>
        <text x={686} y={107} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">E ⊥ B ⊥ direction</text>
        <text x={686} y={123} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">|E|/|B| = c</text>
      </svg>
      <div style={S.dCaption}>
        An EM wave consists of mutually perpendicular oscillating electric (E) and magnetic (B) fields, both perpendicular to the direction of propagation. The ratio |E|/|B| = c always holds. Unlike mechanical waves, EM waves require no medium — they travel through vacuum at c = 299,792,458 m/s.
      </div>
    </div>
  );
}

// ── Diagram 3: Photon Energy vs Frequency ─────────────────────────────
function DiagramPhotonEnergy() {
  const W = 900, H = 280;

  // E = hf: plot points, h = 6.626e-34 J·s, show eV scale
  // We'll do a schematic bar chart of representative photon energies
  const bands2 = [
    { name:'Radio',    eV:'~10⁻⁹ eV',   barH:4,   color:'#1a3a7c' },
    { name:'Microwave',eV:'~10⁻⁵ eV',   barH:10,  color:'#2E75B6' },
    { name:'Infrared', eV:'0.001–1.6 eV',barH:35,  color:'#e07b00' },
    { name:'Visible',  eV:'1.6–3.3 eV',  barH:70,  color:'#c0392b' },
    { name:'UV',       eV:'3.3–124 eV',  barH:130, color:'#8e44ad' },
    { name:'X-ray',    eV:'100 eV–1 MeV',barH:185, color:'#16a085' },
    { name:'Gamma',    eV:'> 1 MeV',     barH:230, color:'#7B0D0D' },
  ];

  const barW = 90, gap = 30, startX = 60, baseY = 248;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Photon Energy by Band — E = hf (logarithmic scale)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Y axis */}
        <line x1={50} y1={18} x2={50} y2={248} stroke="#888" strokeWidth="1.5"/>
        <polygon points="46,18 54,18 50,8" fill="#888"/>
        <text x={5} y={14} fontSize="11" fill="#7B0D0D" fontWeight="700">Energy</text>
        <text x={5} y={26} fontSize="10" fill="#7B0D0D">(log scale)</text>

        {/* Y axis ticks */}
        {['10⁻⁹','10⁻⁵','10⁻¹','10¹','10⁴','10⁶'].map((t, i) => (
          <g key={i}>
            <line x1={46} y1={240-i*40} x2={54} y2={240-i*40} stroke="#888" strokeWidth="1"/>
            <text x={44} y={244-i*40} textAnchor="end" fontSize="10" fill="#555" fontFamily="'Courier New',monospace">{t} eV</text>
          </g>
        ))}

        {/* Bars */}
        {bands2.map((b, i) => {
          const x = startX + i * (barW + gap);
          return (
            <g key={i}>
              <rect x={x} y={baseY - b.barH} width={barW} height={b.barH} fill={b.color} opacity="0.82" rx={2}/>
              <text x={x + barW/2} y={baseY + 14} textAnchor="middle" fontSize="11" fill="#222" fontWeight="700">{b.name}</text>
              <text x={x + barW/2} y={baseY + 27} textAnchor="middle" fontSize="10" fill="#555" fontFamily="'Courier New',monospace">{b.eV}</text>
              {/* Value on top of bar */}
              <text x={x + barW/2} y={baseY - b.barH - 5} textAnchor="middle" fontSize="10" fill={b.color} fontWeight="700"
                fontFamily="'Courier New',monospace">{b.eV.split(' ')[0]}</text>
            </g>
          );
        })}

        {/* Formula box */}
        <rect x={730} y={20} width={160} height={80} rx={4} fill="#FEF0F0" stroke="#c0392b"/>
        <text x={810} y={38} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Photon Energy:</text>
        <text x={740} y={56} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">E = hf = hc/λ</text>
        <text x={740} y={73} fontSize="11" fill="#555">h = 6.626 × 10⁻³⁴ J·s</text>
        <text x={740} y={89} fontSize="11" fill="#555">(Planck's constant)</text>
      </svg>
      <div style={S.dCaption}>
        Photon energy increases linearly with frequency (E = hf). Gamma rays carry around 10¹⁵ times more energy per photon than radio waves. This is why high-frequency radiation (UV, X-ray, gamma) is ionising — photons carry enough energy to remove electrons from atoms.
      </div>
    </div>
  );
}

// ── Diagram 4: Atmospheric Transmission Windows ───────────────────────
function DiagramAtmosphere() {
  const W = 900, H = 260;

  // Schematic transmission profile
  // Regions: blocked (low transmission) vs windows (high transmission)
  // Approximate as filled rects of varying opacity

  const windows = [
    // [x_start, width, transmission_fraction, label, color]
    [0,   60,  0.05, 'γ / X blocked', '#16a085'],
    [60,  60,  0.12, 'UV mostly\nblocked (O₃)', '#8e44ad'],
    [120, 120, 0.95, 'Visible\nWindow', '#c0392b'],
    [240, 80,  0.75, 'Near-IR\npartial', '#e07b00'],
    [320, 60,  0.15, 'IR blocked\n(H₂O, CO₂)', '#e07b00'],
    [380, 70,  0.55, 'Mid-IR\nsome windows', '#e07b00'],
    [450, 60,  0.10, 'Far-IR\nblocked', '#888'],
    [510, 100, 0.92, 'Microwave\nWindow', '#2E75B6'],
    [610, 80,  0.65, 'Some micro-\nwave absorbed', '#2E75B6'],
    [690, 110, 0.98, 'Radio\nWindow', '#1a3a7c'],
  ];

  const barY = 60, barH = 120;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Atmospheric Transmission — Which EM Bands Reach Earth's Surface?</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Background sky gradient */}
        <defs>
          <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d0e8ff"/>
            <stop offset="100%" stopColor="#fff"/>
          </linearGradient>
        </defs>
        <rect x={0} y={barY-10} width={W} height={barH+20} fill="url(#skyGrad)" rx={4}/>

        {/* Transmission bars */}
        {windows.map(([x, w, t, label, color], i) => {
          const filledH = barH * t;
          const labelLines = label.split('\n');
          return (
            <g key={i}>
              {/* Blocked portion (grey) */}
              <rect x={x} y={barY} width={w} height={barH * (1-t)} fill="#ccc" opacity="0.6"/>
              {/* Transmitted portion */}
              <rect x={x} y={barY + barH*(1-t)} width={w} height={filledH} fill={color} opacity="0.7"/>
              {/* Label */}
              {labelLines.map((line, li) => (
                <text key={li} x={x + w/2} y={barY + barH + 20 + li*14}
                  textAnchor="middle" fontSize="10" fill="#333">{line}</text>
              ))}
              {/* Transmission % */}
              <text x={x + w/2} y={barY - 2} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">
                {Math.round(t*100)}%
              </text>
              {/* Divider */}
              {i < windows.length-1 && <line x1={x+w} y1={barY-5} x2={x+w} y2={barY+barH+5} stroke="#ddd" strokeWidth="1"/>}
            </g>
          );
        })}

        {/* Y axis */}
        <line x1={0} y1={barY} x2={0} y2={barY+barH} stroke="#888" strokeWidth="1.5"/>
        <text x={-2} y={barY-3} fontSize="10" fill="#7B0D0D" fontWeight="700">100%</text>
        <text x={-2} y={barY+barH+4} fontSize="10" fill="#7B0D0D">0%</text>
        <text x={-2} y={barY+barH/2} fontSize="10" fill="#7B0D0D" transform={`rotate(-90,-5,${barY+barH/2})`}>Transmission</text>

        {/* Window labels */}
        <rect x={290} y={2} width={120} height={18} rx={3} fill="#fff" stroke="#27ae60" strokeWidth="1"/>
        <text x={350} y={14} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">↑ Transmission windows</text>
      </svg>
      <div style={S.dCaption}>
        The atmosphere is only transparent to certain EM bands. The visible window (380–700 nm) and the radio window (1 mm–10 m) allow ground-based astronomy. Ozone absorbs most UV; water vapour and CO₂ absorb infrared. High-energy γ-rays and X-rays are fully blocked, requiring space telescopes (e.g., Chandra, Fermi).
      </div>
    </div>
  );
}

// ── Diagram 5: Speed, Frequency, Wavelength triangle ─────────────────
function DiagramWaveEquation() {
  const W = 900, H = 240;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Wave Equation — c = fλ: Frequency and Wavelength are Inversely Proportional</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Triangle */}
        <polygon points="200,40 80,200 320,200" fill="#FEF0F0" stroke="#c0392b" strokeWidth="2.5"/>
        <text x={200} y={30} textAnchor="middle" fontSize="22" fill="#7B0D0D" fontFamily="'Courier New',monospace" fontWeight="700">c</text>
        <text x={68} y={215} textAnchor="middle" fontSize="18" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">f</text>
        <text x={332} y={215} textAnchor="middle" fontSize="18" fill="#e07b00" fontFamily="'Courier New',monospace" fontWeight="700">λ</text>
        {/* Divider line */}
        <line x1={80} y1={168} x2={320} y2={168} stroke="#c0392b" strokeWidth="2"/>

        {/* Formulas */}
        <text x={200} y={152} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontFamily="'Courier New',monospace">c = fλ</text>
        <text x={110} y={192} textAnchor="middle" fontSize="13" fill="#2E75B6" fontFamily="'Courier New',monospace">f = c/λ</text>
        <text x={295} y={192} textAnchor="middle" fontSize="13" fill="#e07b00" fontFamily="'Courier New',monospace">λ = c/f</text>

        {/* Worked examples */}
        <rect x={370} y={20} width={510} height={200} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={625} y={42} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Worked Examples</text>
        <line x1={375} y1={48} x2={875} y2={48} stroke="#f0cece" strokeWidth="1"/>

        {[
          { band:'Visible (green light)', f:'5.5 × 10¹⁴ Hz', lambda:'λ = 3×10⁸ / 5.5×10¹⁴ = 545 nm', color:'#27ae60' },
          { band:'FM Radio (100 MHz)',    f:'1.0 × 10⁸ Hz',  lambda:'λ = 3×10⁸ / 1×10⁸ = 3.0 m',     color:'#1a3a7c' },
          { band:'Microwave oven',        f:'2.45 × 10⁹ Hz', lambda:'λ = 3×10⁸ / 2.45×10⁹ = 12.2 cm', color:'#2E75B6' },
          { band:'X-ray (medical)',       f:'3.0 × 10¹⁸ Hz', lambda:'λ = 3×10⁸ / 3×10¹⁸ = 0.1 nm',   color:'#16a085' },
        ].map((ex, i) => (
          <g key={i}>
            <rect x={376} y={56 + i*36} width={496} height={34} fill={i%2===0?'#fff5f5':'#fff'} rx={2}/>
            <text x={383} y={71 + i*36} fontSize="12" fill={ex.color} fontWeight="700">{ex.band}</text>
            <text x={383} y={84 + i*36} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">f = {ex.f}   →   {ex.lambda}</text>
          </g>
        ))}

        {/* c value box */}
        <rect x={376} y={202} width={496} height={14} rx={2} fill="#FEF0F0"/>
        <text x={625} y={213} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontFamily="'Courier New',monospace">c = 299,792,458 m/s ≈ 3.00 × 10⁸ m/s (exact in vacuum)</text>
      </svg>
      <div style={S.dCaption}>
        The wave equation c = fλ applies to all EM radiation. Since c is constant in vacuum, frequency and wavelength are inversely proportional — doubling the frequency halves the wavelength. c is slightly lower in materials (e.g., glass: c/n ≈ 2×10⁸ m/s).
      </div>
    </div>
  );
}

// ── Diagram 6: Applications map ───────────────────────────────────────
function DiagramApplications() {
  const W = 900, H = 310;

  const apps = [
    { band:'Radio',      color:'#1a3a7c', y:30,  items:['AM/FM broadcast','TV signals','Radar','Satellite comms','Astronomy (radio telescopes)'] },
    { band:'Microwave',  color:'#2E75B6', y:30,  items:['Microwave ovens (2.45 GHz)','Wi-Fi / Bluetooth','Mobile networks (4G/5G)','Weather radar','Cosmic Microwave Background'] },
    { band:'Infrared',   color:'#e07b00', y:30,  items:['TV remote controls','Thermal imaging cameras','Optical fibre comms','Night-vision goggles','Heat lamps'] },
    { band:'Visible',    color:'#c0392b', y:30,  items:['Human vision','Photography','Optical microscopy','Lasers (surgery, DVDs)','Traffic lights / LEDs'] },
    { band:'UV',         color:'#8e44ad', y:30,  items:['Sterilisation (kills bacteria)','Fluorescence / black lights','Tanning / Vitamin D synthesis','Spectroscopy','Detecting forged banknotes'] },
    { band:'X-ray',      color:'#16a085', y:30,  items:['Medical imaging (bone, CT)','Airport security scanners','Crystallography (DNA structure!)','Astronomy (Chandra telescope)','Industrial inspection'] },
    { band:'Gamma',      color:'#7B0D0D', y:30,  items:['Cancer radiotherapy','PET scans (medical)','Sterilising medical equipment','Nuclear physics research','γ-ray astronomy (Fermi)'] },
  ];

  const colW = 120, colGap = 8, startX = 8;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Applications of Each Region of the Electromagnetic Spectrum</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {apps.map((a, i) => {
          const x = startX + i * (colW + colGap);
          return (
            <g key={i}>
              {/* Header */}
              <rect x={x} y={10} width={colW} height={28} rx={3} fill={a.color}/>
              <text x={x + colW/2} y={28} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{a.band}</text>
              {/* Items */}
              {a.items.map((item, j) => (
                <g key={j}>
                  <rect x={x} y={44 + j*48} width={colW} height={44} rx={2}
                    fill={j%2===0?'#fff5f5':'#fff'} stroke={a.color} strokeWidth="0.7" opacity="0.9"/>
                  {item.split(' ').reduce((lines, word) => {
                    const last = lines[lines.length-1];
                    if ((last + ' ' + word).length <= 14) {
                      lines[lines.length-1] = last ? last + ' ' + word : word;
                    } else {
                      lines.push(word);
                    }
                    return lines;
                  }, ['']).filter(Boolean).slice(0,3).map((line, li) => (
                    <text key={li} x={x + 5} y={57 + j*48 + li*13} fontSize="10" fill="#333">{line}</text>
                  ))}
                </g>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Every region of the EM spectrum has vital practical and scientific applications. The properties of each band (penetration depth, energy per photon, interaction with matter) determine its uses. Higher-energy photons penetrate further but cause more biological damage — hence strict safety regulations for UV, X-ray, and γ-ray exposure.
      </div>
    </div>
  );
}

// ── Diagram 7: Ionising vs Non-Ionising ──────────────────────────────
function DiagramIonising() {
  const W = 900, H = 200;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Ionising vs Non-Ionising Radiation — Biological Hazards</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Split bar */}
        <rect x={0} y={60} width={440} height={60} fill="#27ae60" opacity="0.8" rx={4}/>
        <text x={220} y={95} textAnchor="middle" fontSize="16" fill="#fff" fontWeight="700">NON-IONISING</text>

        <rect x={450} y={60} width={450} height={60} fill="#c0392b" opacity="0.85" rx={4}/>
        <text x={675} y={95} textAnchor="middle" fontSize="16" fill="#fff" fontWeight="700">IONISING</text>

        {/* Threshold label */}
        <text x={445} y={54} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">~10 eV threshold</text>
        <line x1={445} y1={58} x2={445} y2={122} stroke="#7B0D0D" strokeWidth="2" strokeDasharray="4,2"/>

        {/* Band labels */}
        {[
          { x:30,  label:'Radio', col:'#1a3a7c' },
          { x:130, label:'Micro-\nwave', col:'#2E75B6' },
          { x:240, label:'IR', col:'#e07b00' },
          { x:345, label:'Visible', col:'#888' },
          { x:455, label:'UV\n(high-freq)', col:'#8e44ad' },
          { x:590, label:'X-ray', col:'#16a085' },
          { x:730, label:'Gamma', col:'#7B0D0D' },
        ].map(({x,label,col}) => (
          label.split('\n').map((line,li) => (
            <text key={li} x={x} y={136+li*14} fontSize="11" fill={col} fontWeight="700">{line}</text>
          ))
        ))}

        {/* Risk descriptions */}
        <text x={220} y={168} textAnchor="middle" fontSize="12" fill="#27ae60">Causes heating / molecular vibration only</text>
        <text x={220} y={183} textAnchor="middle" fontSize="11" fill="#555">Does not remove electrons from atoms</text>
        <text x={675} y={168} textAnchor="middle" fontSize="12" fill="#c0392b">Removes electrons → damages DNA → cancer risk</text>
        <text x={675} y={183} textAnchor="middle" fontSize="11" fill="#555">Penetrating power increases with frequency</text>
      </svg>
      <div style={S.dCaption}>
        The ionisation threshold is approximately 10 eV (~ high-energy UV, around 120 nm). Below this, radiation causes thermal effects but not direct DNA damage. Above it, each photon can eject electrons from atoms, breaking chemical bonds and potentially causing mutations. UV-C (100–280 nm) is fully ionising; UV-A/B has intermediate effects.
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────
export default function ElectromagneticSpectrum() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>The Electromagnetic Spectrum</h1>
<img src={ElectromagneticSpectrumImg} alt="Electromagnetic Spectrum" width="75%"/>
      <p style={S.p}>
        Electromagnetic (EM) radiation is energy that propagates through space as coupled oscillating electric and magnetic fields, with no need for a medium. All forms — from radio waves to gamma rays — are the same fundamental phenomenon, differing only in frequency and therefore wavelength and photon energy. Together they form the electromagnetic spectrum.
      </p>

      <InfoBox title="Core Facts" items={[
        "All EM waves travel at c = 3.00 × 10⁸ m/s in vacuum — the fastest possible speed in the universe",
        "EM waves are transverse — oscillations are perpendicular to the direction of travel",
        "The electric and magnetic fields are mutually perpendicular: E ⊥ B ⊥ direction of propagation",
        "The spectrum spans over 20 orders of magnitude in frequency (from ~10³ Hz radio to >10²³ Hz gamma rays)",
        "Visible light — the only band detectable by the human eye — is a tiny sliver of the full spectrum",
        "Photon energy E = hf: higher frequency means more energetic photons (h = 6.626 × 10⁻³⁴ J·s)",
      ]}/>

      <h2 style={S.h2}>Wave Nature — Structure and Propagation</h2>
      <p style={S.p}>
        James Clerk Maxwell (1865) unified electricity and magnetism into four equations, predicting the existence of electromagnetic waves travelling at c. Heinrich Hertz experimentally confirmed this in 1887. EM waves are self-sustaining: a changing electric field generates a magnetic field, which in turn generates an electric field — allowing propagation through empty space.
      </p>
      <DiagramWaveStructure/>
      <Table2 rows={[
        ["Wave speed in vacuum","c = 3.00 × 10⁸ m/s (exact: 299,792,458 m/s)\nDefined in SI — the metre is derived from c"],
        ["Wave equation","c = fλ   →   f = c/λ   →   λ = c/f\nf = frequency (Hz), λ = wavelength (m)"],
        ["Speed in a medium","v = c/n  where n = refractive index of medium\nGlass: n ≈ 1.5 so v ≈ 2.0 × 10⁸ m/s"],
        ["Maxwell's equations","∇·E = ρ/ε₀  |  ∇·B = 0  |  ∇×E = −∂B/∂t  |  ∇×B = μ₀J + μ₀ε₀ ∂E/∂t\nPredict c = 1/√(μ₀ε₀) ≈ 3×10⁸ m/s"],
        ["Transverse wave","E and B oscillate perpendicular to direction of travel\nCan be polarised — unlike longitudinal (sound) waves"],
        ["Polarisation","Linear: E-field oscillates in a single plane\nCircular/elliptical polarisation also possible — used in 3D cinema"],
        ["Intensity","I = P/A = ½cε₀E₀²  (W m⁻²)\nInverse-square law from a point source: I ∝ 1/r²"],
      ]}/>

      <h2 style={S.h2}>The Full Spectrum — Bands and Properties</h2>
      <p style={S.p}>
        The EM spectrum is divided into named regions based on how the radiation is produced and how it interacts with matter. The boundaries between bands are not sharp — they are defined by convention and overlap.
      </p>
      <DiagramSpectrumBar/>
      <DiagramWaveEquation/>

      <h2 style={S.h2}>Photon Energy and Quantum Nature</h2>
      <p style={S.p}>
        Max Planck (1900) and Albert Einstein (1905) established that EM radiation is also quantised — it comes in discrete packets called photons. Each photon carries energy proportional to its frequency. This wave–particle duality is fundamental to quantum mechanics.
      </p>
      <DiagramPhotonEnergy/>
      <Table2 rows={[
        ["Photon energy","E = hf = hc/λ   (unit: Joules or electron-volts, eV)\nh = 6.626 × 10⁻³⁴ J·s (Planck's constant)"],
        ["Electron-volt","1 eV = 1.6 × 10⁻¹⁹ J  (energy gained by electron through 1V)\nConvenient unit for photon and particle energies"],
        ["Photoelectric effect","Photons above a threshold frequency eject electrons from metals\nEinstein (1905): max KE = hf − φ  (φ = work function)"],
        ["Compton scattering","High-energy photons scatter off electrons, losing energy\nWavelength shift: Δλ = (h/mₑc)(1 − cosθ)"],
        ["Momentum of photon","p = E/c = hf/c = h/λ\nPhotons carry momentum despite having no rest mass"],
        ["Wave–particle duality","EM radiation behaves as a wave (diffraction, interference)\nand as particles (photoelectric effect, Compton scattering)"],
        ["Pair production","γ-ray photon → electron + positron  if E > 1.022 MeV\nRequires nearby nucleus to conserve momentum"],
      ]}/>

      <h2 style={S.h2}>Radio Waves</h2>
      <Table2 rows={[
        ["Frequency range","3 Hz – 300 GHz  (wavelengths: 1 mm – 100,000 km)\nSubdivisions: ELF, VLF, LF, MF, HF, VHF, UHF, SHF"],
        ["Production","Oscillating electric charges in antennas\nAlternating current at the transmitting frequency"],
        ["AM radio (MF)","Amplitude modulation at 530–1700 kHz; λ ≈ 200 m\nDiffracts around hills; can reflect off ionosphere"],
        ["FM radio (VHF)","Frequency modulation at 87–108 MHz; λ ≈ 3 m\nLine-of-sight; less noise but limited range"],
        ["Radar","Pulses reflected from targets; time delay → distance\nUsed in aviation, weather, speed cameras"],
        ["Penetration","Can penetrate walls and non-conducting materials\nLong wavelengths diffract around large objects"],
        ["Radio astronomy","Maps hydrogen (21 cm line), pulsars, quasars, CMB\nRequires large dish antennas (Parkes: 64 m diameter)"],
      ]}/>

      <h2 style={S.h2}>Microwaves</h2>
      <Table2 rows={[
        ["Frequency range","300 MHz – 300 GHz  (wavelengths: 1 mm – 1 m)\nOverlap with radio at low end; with IR at high end"],
        ["Microwave oven","2.45 GHz resonates with rotational modes of water molecules\nEnergy absorbed by water → heating; fat and sugar also absorb"],
        ["Wi-Fi / Bluetooth","2.4 GHz and 5 GHz ISM bands\nAbsorbed by water — that's why bodies block signals"],
        ["Mobile networks","2G–5G: 700 MHz to 80 GHz; λ from 40 cm to 3.75 mm\nHigher 5G bands (mmWave) don't penetrate walls"],
        ["Cosmic Microwave\nBackground (CMB)","Relic radiation from 380,000 years after Big Bang\nBlackbody spectrum at T = 2.725 K; peak ≈ 1.9 mm"],
        ["Weather satellites","Microwave imaging sees through cloud cover\nPassive sensing (emitted) and active (radar)"],
      ]}/>

      <h2 style={S.h2}>Infrared</h2>
      <Table2 rows={[
        ["Frequency range","300 GHz – 430 THz  (wavelengths: 700 nm – 1 mm)\nNear-IR (700–2500 nm), mid-IR (2.5–25 μm), far-IR (25–1000 μm)"],
        ["Production","Thermal emission from warm objects\nAll objects above absolute zero emit IR; peak λ from Wien's law"],
        ["Wien's Law","λ_max = b/T   b = 2.898 × 10⁻³ m·K\nHuman body (37°C = 310 K): λ_max ≈ 9.4 μm (mid-IR)"],
        ["Stefan-Boltzmann","P = σAT⁴   σ = 5.67 × 10⁻⁸ W m⁻² K⁻⁴\nTotal power radiated per unit area scales as T⁴"],
        ["Thermal cameras","Detect temperature differences of < 0.1°C\nUsed in search-and-rescue, building inspection, medical diagnosis"],
        ["Greenhouse effect","CO₂, H₂O, CH₄ absorb outgoing IR from Earth's surface\nVibrations of molecular bonds match IR frequencies"],
        ["Optical fibres","Near-IR (1310 nm, 1550 nm) used in fibre-optic comms\nMinimum absorption and dispersion in silica glass at these wavelengths"],
      ]}/>

      <h2 style={S.h2}>Visible Light</h2>
      <p style={S.p}>
        The visible spectrum (approximately 380–700 nm) is the narrow band to which the human eye is sensitive — an evolutionary adaptation to the peak of solar output. Different wavelengths correspond to different perceived colours.
      </p>
      <Table2 rows={[
        ["Visible range","380–700 nm  (430–770 THz)\nViolet (380–450 nm) → Red (620–700 nm)"],
        ["Colour & wavelength","Violet: 380–450 nm | Blue: 450–495 nm | Green: 495–570 nm\nYellow: 570–590 nm | Orange: 590–620 nm | Red: 620–700 nm"],
        ["Speed in glass","v = c/n; n(glass) ≈ 1.5; n varies with λ → dispersion\nShort λ (violet) slows more → larger refraction angle"],
        ["Snell's Law","n₁ sinθ₁ = n₂ sinθ₂\nGoverns refraction at interfaces; basis for lenses and prisms"],
        ["Dispersion (prism)","Different wavelengths refracted by different amounts\nWhite light → spectrum (Newton, 1666)"],
        ["Interference","Constructive: path difference = nλ  |  Destructive: (n+½)λ\nDemonstrated by Young's double-slit experiment (1801)"],
        ["Diffraction","Spreading of waves around obstacles/slits\nSignificant when slit width ≈ λ; basis of diffraction gratings"],
        ["Photosynthesis","Chlorophyll absorbs red (680 nm) and blue (430 nm) most strongly\nGreen is reflected — hence plant colour"],
      ]}/>

      <h2 style={S.h2}>Ultraviolet</h2>
      <Table2 rows={[
        ["Frequency range","770 THz – 30 PHz  (wavelengths: 10–380 nm)\nUV-A: 315–380 nm | UV-B: 280–315 nm | UV-C: 100–280 nm"],
        ["Source","Sun (most blocked by ozone layer)\nUV lamps (mercury-vapour), welding arcs"],
        ["Ozone layer","O₃ absorbs UV-B and UV-C via photodissociation\nO₃ + hf → O₂ + O  — prevents most UV-C reaching surface"],
        ["Biological effects","UV-B triggers vitamin D synthesis in skin\nUV-B/C damages DNA → mutations, sunburn, skin cancer"],
        ["UV-C sterilisation","254 nm matches DNA absorption peak — kills bacteria/viruses\nUsed in hospitals, water treatment, food processing"],
        ["Fluorescence","UV photon absorbed; lower-energy visible photon emitted\nUsed in banknote security features, fluorescent lighting, spectroscopy"],
        ["Ionisation threshold","High-frequency UV (< ~120 nm) is ionising\nSolar UV-A/B: partially ionising at high doses"],
      ]}/>

      <h2 style={S.h2}>X-Rays</h2>
      <Table2 rows={[
        ["Frequency range","30 PHz – 30 EHz  (wavelengths: 0.01–10 nm)\nSoft X-rays: 0.1–10 nm | Hard X-rays: 0.01–0.1 nm"],
        ["Production","Electrons decelerated by a metal target (bremsstrahlung)\nCharacteristic X-rays from electron shell transitions"],
        ["Penetration","Pass through soft tissue; absorbed by denser bone (Ca)\nHarder X-rays penetrate more — adjustable via tube voltage"],
        ["Medical imaging","Chest X-rays, mammography, CT (computed tomography)\nCT: many 2D X-ray images reconstructed into 3D volume"],
        ["Crystallography","Bragg's Law: 2d sinθ = nλ  (d = plane spacing)\nX-ray diffraction determined DNA double-helix structure (Franklin, 1952)"],
        ["Photoelectric absorption","X-ray photon ejects inner-shell electron\nAbsorption edge depends on atomic number Z"],
        ["Radiation dose","Measured in Gray (Gy = J/kg) or Sievert (Sv = weighted dose)\nChest X-ray ≈ 0.02 mSv; CT scan ≈ 2–20 mSv"],
      ]}/>

      <h2 style={S.h2}>Gamma Rays</h2>
      <Table2 rows={[
        ["Frequency range","f > 30 EHz  (λ < 0.01 nm, energies > 100 keV)\nHighest-energy EM radiation; produced by nuclear processes"],
        ["Production","Nuclear decay (radioactive isotopes: ⁶⁰Co, ¹³⁷Cs)\nAnnihilation: e⁺ + e⁻ → 2γ  (each 511 keV)\nAstrophysical processes: supernovae, neutron star mergers, GRBs"],
        ["Interactions with matter","Photoelectric (low E), Compton scattering (mid E),\nPair production (E > 1.022 MeV) — all remove energy"],
        ["Shielding","Dense high-Z materials (lead, concrete)\nHalf-value layer for ⁶⁰Co in lead ≈ 11 mm"],
        ["PET scanning","⁺ emitter injected → annihilation → two 511 keV γ → coincidence detection\nMaps metabolic activity in brain, tumours"],
        ["Radiotherapy","Targeted γ-ray beams destroy cancer cells\nStereotactic radiosurgery (Gamma Knife): >200 beams focused on tumour"],
        ["Gamma-ray bursts","Most energetic explosions in the universe (up to 10⁴⁴ J in seconds)\nLong GRBs: collapsing massive stars | Short GRBs: neutron star mergers"],
      ]}/>

      <h2 style={S.h2}>Atmospheric Transmission</h2>
      <p style={S.p}>
        The Earth's atmosphere is not equally transparent to all EM bands. This has profound implications for astronomy, remote sensing, and communication.
      </p>
      <DiagramAtmosphere/>

      <h2 style={S.h2}>Ionising vs Non-Ionising Radiation</h2>
      <DiagramIonising/>
      <Table2 rows={[
        ["Ionisation threshold","≈ 10 eV photon energy (λ ≈ 120 nm)\nPhotons below this cannot remove electrons from atoms"],
        ["Non-ionising hazards","Microwave/RF: dielectric heating (tissue warming)\nUV-A: reactive oxygen species, indirect DNA damage"],
        ["Ionising hazards","Direct strand breaks in DNA → mutations, cell death\nRisk proportional to dose (Sv); no safe threshold assumed"],
        ["Inverse-square law","I ∝ 1/r²  — intensity drops rapidly with distance\nDoubling distance quarters intensity"],
        ["Shielding principles","Radio/microwave: conducting metal (Faraday cage)\nX-ray/gamma: dense high-Z material (lead); plus distance and time"],
        ["Background radiation","~2.4 mSv/year average (varies by location)\nSources: radon gas (largest), cosmic rays, food, medical"],
      ]}/>

      <h2 style={S.h2}>Applications of the EM Spectrum</h2>
      <DiagramApplications/>

      <h2 style={S.h2}>Historical Development</h2>
      <Table2 rows={[
        ["1665 — Newton","Prism experiment: white light is a mixture of colours\nFirst evidence of spectrum structure"],
        ["1800 — Herschel","Discovered infrared: thermometer beyond red end of spectrum\nFirst evidence of radiation beyond visible"],
        ["1801 — Ritter","Discovered ultraviolet: silver chloride blackened beyond violet end"],
        ["1864 — Maxwell","Predicted existence of electromagnetic waves from theory\nCalculated their speed = c; unified E&M"],
        ["1887 — Hertz","Generated and detected radio waves experimentally\nConfirmed Maxwell's predictions"],
        ["1895 — Röntgen","Discovered X-rays; first medical X-ray of wife's hand\nNobel Prize in Physics 1901"],
        ["1900 — Planck","Quantisation of radiation: E = hf — birth of quantum physics\nSolved the ultraviolet catastrophe"],
        ["1905 — Einstein","Photoelectric effect explained by photons\nWave–particle duality established"],
        ["1912 — von Laue","X-ray diffraction by crystals — revealed atomic structure\nBragg's Law: 2d sinθ = nλ"],
        ["1965 — Penzias & Wilson","Detected Cosmic Microwave Background at 7.35 cm\nConfirmed Big Bang theory; Nobel Prize 1978"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Wave equation:  c = fλ  →  f = c/λ  →  λ = c/f   (c = 3.00 × 10⁸ m/s)",
        "Photon energy:  E = hf = hc/λ   (h = 6.626 × 10⁻³⁴ J·s  =  4.136 × 10⁻¹⁵ eV·s)",
        "Wien's Displacement Law:  λ_max = b/T   (b = 2.898 × 10⁻³ m·K)",
        "Stefan-Boltzmann Law:  P = σAT⁴   (σ = 5.67 × 10⁻⁸ W m⁻² K⁻⁴)",
        "Snell's Law:  n₁ sinθ₁ = n₂ sinθ₂   (refraction at interfaces)",
        "Bragg's Law:  2d sinθ = nλ   (X-ray crystallography, d = plane spacing)",
        "Photoelectric effect:  KE_max = hf − φ   (φ = work function of metal)",
        "Compton shift:  Δλ = (h/mₑc)(1 − cosθ)   (mₑ = 9.109 × 10⁻³¹ kg)",
        "Speed in medium:  v = c/n   (n = refractive index, n ≥ 1)",
        "Intensity:  I = P/A = ½cε₀E₀²   →   I ∝ 1/r²   (inverse-square law)",
      ]}/>
    </div>
  );
}